import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import LoadSpinner from "../../../../utilities/LoadSpinner";
import { getRightworkAPI } from "../../requests/utilities/requests";
import { formatDate } from "../../requests/utilities/helpers";
import { API_get_logbook_email_preview } from "../../../../constants";

const boxStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 720,
  maxWidth: "92vw",
  height: "85vh",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

// Renders the daily logbook email exactly as recipients get it: same payload
// builder, same template, for the selected store and yesterday.
function EmailPreviewModal(props) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [html, setHtml] = React.useState(null);
  const [dateLabel, setDateLabel] = React.useState(null);
  const [wouldSend, setWouldSend] = React.useState(true);

  // Identity of the preview currently wanted, assigned on every render: a
  // slow request (previews can wait on an agent run) resolving after the
  // modal was reopened for another store/date - or closed - must not
  // overwrite the newer preview's html, date label, or delivery status.
  const currentKeyRef = React.useRef("");
  currentKeyRef.current = props.open
    ? `${props.homeStore}|${props.date ? formatDate(props.date) : ""}`
    : "";

  React.useEffect(() => {
    if (!props.open) {
      return;
    }
    const fetchPreview = async () => {
      const requestKey = `${props.homeStore}|${props.date ? formatDate(props.date) : ""}`;
      setIsLoading(true);
      setHtml(null);
      setWouldSend(true);
      try {
        const res = await getRightworkAPI(API_get_logbook_email_preview, {
          store_id: props.homeStore,
          // Follow the hub's selected date; without one the backend defaults
          // to yesterday (what the real morning email covers).
          ...(props.date && { date: formatDate(props.date) }),
        });
        if (currentKeyRef.current !== requestKey) return;
        if (res.status === 200) {
          setHtml(res.data?.redux?.payload?.html);
          setDateLabel(res.data?.redux?.payload?.date);
          setWouldSend(res.data?.redux?.payload?.would_send !== false);
        }
      } catch (error) {
        if (currentKeyRef.current !== requestKey) return;
        console.error("Email preview failed", error);
      } finally {
        if (currentKeyRef.current === requestKey) {
          setIsLoading(false);
        }
      }
    };
    fetchPreview();
  }, [props.open, props.homeStore, props.date]);

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <Box sx={boxStyle}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <div>
            <p className="text-sm font-bold text-slate-900">
              Daily email preview
            </p>
            <p className="text-xs text-slate-500">
              {dateLabel ? `Covering ${dateLabel}` : "Covering yesterday"} ·
              exactly what recipients get
            </p>
          </div>
          <div
            className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center cursor-pointer hover:bg-slate-200"
            onClick={props.onClose}
          >
            <CloseIcon style={{ fontSize: "18px", color: "#64748B" }} />
          </div>
        </div>
        <div className="flex-1 relative bg-slate-50">
          {isLoading ? (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <LoadSpinner />
            </div>
          ) : !wouldSend ? (
            <p className="text-sm text-slate-500 text-center pt-20 px-10">
              Nothing in this layout is included in the email, so no daily
              email would be sent for this store. Toggle the mail icon on
              blocks in Configure to include them.
            </p>
          ) : html ? (
            <iframe
              title="Logbook email preview"
              srcDoc={html}
              className="w-full h-full border-0"
              sandbox=""
            />
          ) : (
            <p className="text-sm text-slate-500 text-center pt-20">
              The preview could not be generated.
            </p>
          )}
        </div>
      </Box>
    </Modal>
  );
}

export default EmailPreviewModal;
